/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@/shared/ui/radix-icons';
import { Button, SegmentedControl, Text, UnstyledButton, useAreaIntroToggle } from '@/shared/ui';
import type { SegmentedControlItem } from '@/shared/ui';
import type { AgendaView } from '@features/agenda/utils/agenda-dates';

interface AgendaToolbarProps {
  view: AgendaView;
  label: string;
  onViewChange: (view: AgendaView) => void;
  onNavigate: (direction: 1 | -1) => void;
  onToday: () => void;
}

const VIEWS: AgendaView[] = ['day', 'week', 'month', 'list'];

const NAV_BTN =
  'flex h-7 w-7 items-center justify-center rounded-[var(--radius-md)] border border-[var(--border-subtle)] text-[var(--text-secondary)] transition-colors hover:bg-[var(--surface-secondary)] hover:text-[var(--text-primary)]';

/**
 * Top toolbar: period navigation and day/week/month switcher.
 *
 * @param props Toolbar state and handlers.
 * @returns Rendered toolbar.
 */
export const AgendaToolbar: React.FC<AgendaToolbarProps> = ({
  view,
  label,
  onViewChange,
  onNavigate,
  onToday,
}) => {
  const { t } = useTranslation('features/agenda');
  const intro = useAreaIntroToggle({
    label: t('intro.label'),
    body: t('intro.body'),
    testIdPrefix: 'agenda',
  });

  const viewItems: SegmentedControlItem<AgendaView>[] = useMemo(
    () => VIEWS.map((v) => ({ value: v, label: t(`view.${v}`) })),
    [t],
  );

  return (
    <div className="flex flex-col gap-(--space-3)">
      <div className="flex flex-wrap items-center justify-between gap-(--space-2-5)">
        <div data-testid="toolbar-nav-cluster" className="flex items-center gap-(--space-3)">
          <div className="flex items-center gap-(--space-1-5)">
            <UnstyledButton
              aria-label={t('toolbar.previous')}
              onClick={() => onNavigate(-1)}
              className={NAV_BTN}
            >
              <ChevronLeftIcon width={14} height={14} />
            </UnstyledButton>
            <UnstyledButton
              aria-label={t('toolbar.next')}
              onClick={() => onNavigate(1)}
              className={NAV_BTN}
            >
              <ChevronRightIcon width={14} height={14} />
            </UnstyledButton>
          </div>
          <Text span size="sm" fw={700}>
            {label}
          </Text>
          {intro.accessory}
          <Button variant="outlined" size="xs" onClick={onToday}>
            {t('toolbar.today')}
          </Button>
        </div>
        <SegmentedControl<AgendaView>
          value={view}
          onChange={onViewChange}
          items={viewItems}
          size="sm"
          ariaLabel={t('toolbar.viewSwitcher')}
          testId="agenda-view-switcher"
        />
      </div>
      {intro.panel}
    </div>
  );
};
